import { useEffect, useState } from 'react';
import { UPDATE_CHECK_EVENT } from '../lib/refresh';
import { checkForUpdate, installUpdate, type Latest } from '../lib/update';
import { Button } from './Button';
import { Sheet } from './Sheet';
import s from './UpdatePrompt.module.css';

/** "Later" holds until the app is next opened. */
let dismissedVersion: number | null = null;

const mb = (bytes: number) => `${(bytes / 1_048_576).toFixed(1)} MB`;
const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);

/**
 * Android: when a newer build is on the server, offer it as soon as the app opens (or comes
 * back to the front), with what's new and a progress bar while it downloads. Android then
 * shows its own Install screen.
 */
export function UpdatePrompt() {
  const [latest, setLatest] = useState<Latest | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [state, setState] = useState<'offer' | 'downloading' | 'permission' | 'error'>('offer');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const check = () =>
      void checkForUpdate().then((l) => {
        if (l && l.versionCode !== dismissedVersion) setLatest(l);
      });
    check();
    // Pull to refresh asks again, even after "Later".
    const again = () => {
      dismissedVersion = null;
      check();
    };
    window.addEventListener(UPDATE_CHECK_EVENT, again);
    let remove: (() => void) | undefined;
    void import('@capacitor/app').then(({ App }) =>
      App.addListener('resume', check).then((h) => {
        remove = () => void h.remove();
      }),
    );
    return () => {
      remove?.();
      window.removeEventListener(UPDATE_CHECK_EVENT, again);
    };
  }, []);

  if (!latest) return null;

  const close = () => {
    if (state === 'downloading') return;
    dismissedVersion = latest.versionCode;
    setLatest(null);
    setState('offer');
  };

  const start = async () => {
    setState('downloading');
    setProgress(0);
    setError(null);
    try {
      const result = await installUpdate(latest, setProgress);
      if (result === 'needs-permission') setState('permission');
      else {
        // Android's installer is up; if it's cancelled the offer comes back next time.
        setState('offer');
        setLatest(null);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Couldn't download the update");
      setState('error');
    } finally {
      setProgress(null);
    }
  };

  const all = latest.notes ?? [];
  const notes = all.slice(0, 6);
  const share = progress ?? 0;

  return (
    <Sheet open onClose={close} title="Update available">
      <div className={s.body}>
        <p className={s.lead}>
          Version {latest.versionName} is ready, {mb(latest.bytes)}.
        </p>
        {notes.length > 0 && state !== 'downloading' && (
          <ul className={s.notes}>
            {notes.map((n) => (
              <li key={n}>{sentence(n)}</li>
            ))}
            {all.length > notes.length && <li>And {all.length - notes.length} more</li>}
          </ul>
        )}
        {state === 'downloading' && (
          <div className={s.progress} role="progressbar" aria-valuenow={Math.round(share * 100)}>
            <span className={s.bar}>
              <span style={{ width: `${Math.max(3, Math.round(share * 100))}%` }} />
            </span>
            <span className={`${s.count} num`}>
              {mb(share * latest.bytes)} of {mb(latest.bytes)}, {Math.round(share * 100)}%
            </span>
          </div>
        )}
        {state === 'permission' && (
          <p className={s.hint}>
            Turn on "Allow from this source" for Trips App, come back, then tap Update again.
          </p>
        )}
        {state === 'error' && error && <p className={s.error}>{error}</p>}
        <div className={s.actions}>
          {state !== 'downloading' && (
            <Button variant="ghost" onClick={close}>
              Later
            </Button>
          )}
          <Button variant="primary" wide disabled={state === 'downloading'} onClick={start}>
            {state === 'downloading'
              ? 'Downloading'
              : state === 'error' || state === 'permission'
                ? 'Try again'
                : 'Update'}
          </Button>
        </div>
      </div>
    </Sheet>
  );
}
